import type {
  LeadFormAttributes,
  LeadFormFieldAttributes,
} from '../../types/strapi'
import type { LeadFormConfig, LeadFormField, LeadFormOption } from './types'

const DEFAULT_SUBMIT_LABEL = 'Submit'
const DEFAULT_SUCCESS_MESSAGE = 'Thank you, we will be in touch shortly!'

function mapOptions(
  field: LeadFormFieldAttributes,
  productNames: string[],
): LeadFormOption[] {
  const listOptions =
    field.optionList?.data?.attributes?.options?.map(({ label, value }) => ({
      label,
      value,
    })) ?? []

  if (field.optionsFrom !== 'publishedProducts') return listOptions

  const productOptions = productNames.map(name => ({
    label: name,
    value: name,
  }))
  return [...productOptions, ...listOptions]
}

function mapField(
  field: LeadFormFieldAttributes,
  productNames: string[],
): LeadFormField {
  const requiredWhen =
    field.requiredWhenField && field.requiredWhenValue
      ? { field: field.requiredWhenField, value: field.requiredWhenValue }
      : undefined

  return {
    salesforceName: field.salesforceName,
    label: field.label?.trim() || field.salesforceName,
    type: field.type,
    required: Boolean(field.required),
    helperText: field.helperText?.trim() || undefined,
    options: mapOptions(field, productNames),
    defaultValue: field.defaultValue ?? '',
    dynamicValue: field.dynamicValue ?? 'none',
    requiredWhen,
    sendToSalesforce: field.sendToSalesforce !== false,
  }
}

/**
 * Turns a CMS Lead Form entry into a renderable form. Returns null when the
 * entry has no lead source or no fields, so a half-built form never renders.
 */
export function mapLeadForm(
  attributes: LeadFormAttributes | null | undefined,
  productNames: string[] = [],
): LeadFormConfig | null {
  const fields = attributes?.fields?.filter(field => field.salesforceName)
  if (!attributes?.leadSource?.trim() || !fields?.length) return null

  return {
    name: attributes.name,
    leadSource: attributes.leadSource.trim(),
    fields: fields.map(field => mapField(field, productNames)),
    submitLabel: attributes.submitLabel?.trim() || DEFAULT_SUBMIT_LABEL,
    successMessage:
      attributes.successMessage?.trim() || DEFAULT_SUCCESS_MESSAGE,
    afterSubmit: attributes.afterSubmit ?? 'stay',
    redirectUrl: attributes.redirectUrl?.trim() || undefined,
  }
}
